import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, UserCheck, AlertTriangle, X, Pencil } from "lucide-react";
import api from "@/lib/api";

/**
 * Reassign an event to a different Project Manager.
 *
 * Visible to admin + regional_pm. The backend recomputes regional_pm_id
 * automatically from the new PM's parent, so the event cleanly transfers.
 */
export function ReassignEventModal({ event, projectManagers, onClose, onDone }) {
  const [pmId, setPmId] = useState("");
  const [busy, setBusy] = useState(false);
  const currentPm = projectManagers.find((p) => p.user_id === event.pm_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!pmId) { toast.error("Pick a new Project Manager"); return; }
    if (pmId === event.pm_id) { toast.error("That's already the current PM"); return; }
    setBusy(true);
    try {
      await api.patch(`/events/${event.event_id}`, { pm_id: pmId });
      const newPm = projectManagers.find((p) => p.user_id === pmId);
      toast.success(`"${event.name}" handed over to ${newPm?.name || "new PM"}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reassignment failed");
    } finally {
      setBusy(false);
    }
  };

  const candidates = projectManagers
    .filter((p) => p.active !== false && p.user_id !== event.pm_id)
    .sort((a, b) => (a.city || "").localeCompare(b.city || "") || a.name.localeCompare(b.name));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="reassign-event-modal"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
            <UserCheck className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-black text-lg">Reassign Event</h3>
            <p className="text-xs text-slate-500 mt-0.5">Clean handover — all data (proposal, budget, allotments, posts) transfers to the new PM.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full" data-testid="reassign-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="bg-slate-50 rounded-xl p-3 mb-4 text-sm">
          <div className="font-bold">{event.name}</div>
          <div className="text-xs text-slate-500">{event.school_name} · {event.start_date} → {event.end_date}</div>
          <div className="text-xs mt-1.5">
            <span className="text-slate-400">Currently with:</span>{" "}
            <span className="font-semibold">{currentPm?.name || event.pm_name || "Unassigned"}</span>
            {currentPm?.city && <span className="text-slate-400"> · {currentPm.city}</span>}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Hand over to *</label>
            <select required value={pmId} onChange={(e) => setPmId(e.target.value)} className="input-pill" data-testid="reassign-pm-select" autoFocus>
              <option value="">Select a Project Manager…</option>
              {candidates.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {p.name} · {p.city || "—"} · {p.email}
                </option>
              ))}
            </select>
            {candidates.length === 0 && (
              <div className="text-xs text-rose-600 mt-1">No other active Project Managers available.</div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 py-2.5">Cancel</button>
            <button type="submit" disabled={busy || !pmId || candidates.length === 0} className="btn-primary flex-1 py-2.5 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="reassign-submit">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Reassigning…</> : <><UserCheck className="w-4 h-4" /> Reassign</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

/**
 * Edit event details — name, school, address, dates, contact, delegate
 * targets + fees. Visible to admin + regional_pm. Sends a PATCH with only
 * the fields the user actually changed so we never wipe blank optional
 * columns on the server.
 */
export function EditEventModal({ event, onClose, onDone }) {
  const [form, setForm] = useState({
    name: event.name || "",
    address: event.address || "",
    email: event.email || "",
    school_name: event.school_name || "",
    school_email: event.school_email || "",
    start_date: event.start_date || "",
    end_date: event.end_date || "",
    expected_delegates: event.expected_delegates ?? 200,
    delegate_fee: event.delegate_fee ?? 2500,
    school_delegate_fee: event.school_delegate_fee ?? "",
    host_school_delegate_fee: event.host_school_delegate_fee ?? "",
    kaimera_fee_per_delegate: event.kaimera_fee_per_delegate ?? "",
  });
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Only send fields that actually changed — avoids wiping nullable
    // columns the user didn't touch.
    const diff = {};
    for (const k of Object.keys(form)) {
      const orig = event[k];
      let val = form[k];
      if (["expected_delegates"].includes(k)) val = parseInt(val) || 0;
      if (["delegate_fee", "school_delegate_fee", "host_school_delegate_fee", "kaimera_fee_per_delegate"].includes(k)) {
        val = val === "" ? null : parseFloat(val);
      }
      // Skip when unchanged (loose-equal to absorb empty-string vs null)
      if (val == orig) continue;
      if (val === "" && orig == null) continue;
      diff[k] = val;
    }
    if (Object.keys(diff).length === 0) { toast.info("Nothing to update"); return; }
    setBusy(true);
    const doPatch = async (body) => api.patch(`/events/${event.event_id}`, body);
    try {
      try {
        await doPatch(diff);
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 409 && d?.code === "kaimera_fee_change_needs_confirm") {
          const ok = window.confirm(
            `${d.message}\n\nProceed with the Kaimera fee change?`
          );
          if (!ok) { setBusy(false); return; }
          await doPatch({ ...diff, force_kaimera_fee_change: true });
        } else {
          throw e;
        }
      }
      toast.success(`Updated "${form.name}"`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-event-modal"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
            <Pencil className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-black text-lg">Edit event</h3>
            <p className="text-xs text-slate-500 mt-0.5">Only changed fields will be saved.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full" data-testid="edit-close"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Event name</label>
            <input required value={form.name} onChange={(e) => setField("name", e.target.value)} className="input-pill" data-testid="edit-ev-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Address</label>
            <input value={form.address} onChange={(e) => setField("address", e.target.value)} className="input-pill" data-testid="edit-ev-address" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Event email</label>
              <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="input-pill" data-testid="edit-ev-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">School email</label>
              <input type="email" value={form.school_email || ""} onChange={(e) => setField("school_email", e.target.value)} className="input-pill" data-testid="edit-ev-school-email" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Host school</label>
            <input value={form.school_name} onChange={(e) => setField("school_name", e.target.value)} className="input-pill" data-testid="edit-ev-school" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Start date</label>
              <input type="date" value={form.start_date} onChange={(e) => setField("start_date", e.target.value)} className="input-pill" data-testid="edit-ev-start" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">End date</label>
              <input type="date" value={form.end_date} onChange={(e) => setField("end_date", e.target.value)} className="input-pill" data-testid="edit-ev-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Expected delegates</label>
              <input type="number" min={1} value={form.expected_delegates} onChange={(e) => setField("expected_delegates", e.target.value)} className="input-pill" data-testid="edit-ev-delegates" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Individual fee (₹)</label>
              <input type="number" min={0} value={form.delegate_fee} onChange={(e) => setField("delegate_fee", e.target.value)} className="input-pill" data-testid="edit-ev-fee" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">School fee (₹)</label>
              <input type="number" min={0} value={form.school_delegate_fee} onChange={(e) => setField("school_delegate_fee", e.target.value)} className="input-pill" placeholder="—" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Host fee (₹)</label>
              <input type="number" min={0} value={form.host_school_delegate_fee} onChange={(e) => setField("host_school_delegate_fee", e.target.value)} className="input-pill" placeholder="—" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Kaimera /delegate (₹)</label>
              <input type="number" min={0} value={form.kaimera_fee_per_delegate} onChange={(e) => setField("kaimera_fee_per_delegate", e.target.value)} className="input-pill" placeholder="—" />
            </div>
          </div>

          <div className="flex gap-2 pt-3">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 py-2.5">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1 py-2.5 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="edit-submit">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Pencil className="w-4 h-4" /> Save changes</>}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

/**
 * Hard-delete an event. Type the event name to confirm.
 *
 * Cascades to every related collection (committees, allotments, budgets,
 * social posts, vendors, blogs, etc.) — irreversible.
 */
export function DeleteEventModal({ event, onClose, onDone }) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  // Production event names sometimes carry trailing spaces, non-breaking
  // spaces (\u00A0), or differ in case from what the user typed in the
  // confirm box. Normalising both sides (collapse whitespace + lowercase)
  // keeps the comparison forgiving while still proving intent.
  const _norm = (s) =>
    (s || "")
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  const namesMatch = _norm(confirmText) === _norm(event.name) && confirmText.trim().length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!namesMatch) { toast.error("Type the event name exactly to confirm"); return; }
    setBusy(true);
    try {
      const r = await api.delete(`/events/${event.event_id}`);
      const counts = r.data?.deleted_counts || r.data?.deleted || {};
      const totalCascaded = Object.entries(counts).filter(([k]) => k !== "event").reduce((s, [, v]) => s + (v || 0), 0);
      toast.success(`"${event.name}" deleted${totalCascaded ? ` (+ ${totalCascaded} related records)` : ""}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-event-modal"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-black text-lg text-rose-700">Permanently delete event?</h3>
            <p className="text-xs text-slate-500 mt-0.5">Hard delete — cannot be undone. All committees, allotments, budget, vendors, social posts, blogs, attendance and marking records will be wiped.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-full" data-testid="delete-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 mb-4">
          <div className="font-bold text-rose-900">{event.name}</div>
          <div className="text-xs text-rose-700/80">{event.school_name} · {event.start_date} → {event.end_date}</div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs text-slate-600">
              Type <code className="bg-slate-100 px-1.5 py-0.5 rounded font-mono text-rose-600">{event.name}</code> to confirm:
            </span>
            <input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={event.name}
              className="input-pill mt-1.5"
              data-testid="delete-confirm-input"
            />
            {confirmText && !namesMatch && (
              <div className="text-[11px] text-rose-600 mt-1">
                Doesn&apos;t match yet — expected exactly: <code className="bg-rose-50 px-1 rounded">{event.name}</code>
              </div>
            )}
            {namesMatch && (
              <div className="text-[11px] text-emerald-600 mt-1">✓ Match — you can delete now.</div>
            )}
          </label>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 py-2.5">Cancel</button>
            <button
              type="submit"
              disabled={busy || !namesMatch}
              className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-semibold disabled:bg-slate-200 disabled:text-slate-400 transition flex items-center justify-center gap-2"
              data-testid="delete-submit"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Deleting…</> : "Delete event"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
